import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';

interface SEODataProps {
  pageTitle?: string;
  pageDescription?: string;
  canonicalPath?: string;
  pageType?: 'website' | 'article' | 'about' | 'category';
  articleData?: {
    title: string;
    author: string;
    publishDate: string;
    imageUrl: string;
  };
  breadcrumbs?: { name: string; url: string }[];
  faqs?: { question: string; answer: string }[];
}

const SITE_NAME = '妈妈的朋友';
const SITE_URL = 'https://mamadepengyou.com';

export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    description: '专注于影视资讯、深度影评和观影指南的中文影视内容平台',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-123-4567',
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
    address: {
      '@type': 'PostalAddress',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      streetAddress: '建国路88号影视大厦12层',
      addressCountry: 'CN',
    },
  };
}

export function generateWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

export function generateWebPageSchema(
  title: string,
  description: string,
  url: string,
  breadcrumbs?: { name: string; url: string }[]
) {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: url,
    inLanguage: 'zh-CN',
  };

  if (breadcrumbs && breadcrumbs.length > 0) {
    schema.breadcrumb = {
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    };
  }

  return schema;
}

export function generateArticleSchema(
  title: string,
  author: string,
  publishDate: string,
  imageUrl: string,
  url: string,
  description: string
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    author: {
      '@type': 'Person',
      name: author,
    },
    datePublished: publishDate,
    image: imageUrl,
    url: url,
    description: description,
    inLanguage: 'zh-CN',
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
  };
}

export function generateFAQSchema(faqs: { question: string; answer: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

export default function SEOData({
  pageTitle,
  pageDescription,
  canonicalPath,
  pageType = 'website',
  articleData,
  breadcrumbs,
  faqs,
}: SEODataProps) {
  const location = useLocation();
  const currentUrl = `${SITE_URL}${canonicalPath || location.pathname}`;

  const schemas = useMemo(() => {
    const result: Record<string, unknown>[] = [];

    result.push(generateOrganizationSchema());
    result.push(generateWebsiteSchema());

    const title = pageTitle || SITE_NAME;
    const description =
      pageDescription ||
      '妈妈的朋友影视平台 - 提供高清影视资讯、深度影评和观影指南';

    result.push(generateWebPageSchema(title, description, currentUrl, breadcrumbs));

    if (pageType === 'article' && articleData) {
      result.push(
        generateArticleSchema(
          articleData.title,
          articleData.author,
          articleData.publishDate,
          articleData.imageUrl,
          currentUrl,
          description
        )
      );
    }

    if (faqs && faqs.length > 0) {
      result.push(generateFAQSchema(faqs));
    }

    return result;
  }, [
    pageTitle,
    pageDescription,
    canonicalPath,
    pageType,
    articleData,
    breadcrumbs,
    faqs,
    currentUrl,
  ]);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schemas, null, 2),
      }}
    />
  );
}